import * as THREE from "three";
import type { Cup } from "./types";
import type { World as SceneWorld } from "./scene";
import type { Hud } from "./hud";
import { searchCups, type SearchMatch } from "./search";
import { applySpotlight, clearSpotlight } from "./search-highlight";

// Search input → live spotlight on matching cup bodies; search result
// pick → cup focus via the shared `selectCup` path. Returns a small
// handle so main.ts can re-apply the spotlight when the cat GLB
// finishes loading mid-search (the cat's materials weren't in the
// scene at apply time and would otherwise read at full brightness
// against a dimmed shelf).

export interface SearchDeps {
  cups: Cup[];
  cupMap: Map<string, { group: THREE.Group; cup: Cup }>;
  hud: Hud;
  world: SceneWorld;
  selectCup: (cup: Cup | null) => void;
}

export interface SearchHandle {
  /** Re-apply the spotlight against the current set of search matches.
   * Used by the cat-load callback so the cat doesn't render bright. */
  reapplyForLateMaterials: () => void;
}

export function attachSearch(deps: SearchDeps): SearchHandle {
  const { cups, cupMap, hud, world, selectCup } = deps;

  let activeMatches: SearchMatch[] = [];

  function buildSpotlightExempt(matches: SearchMatch[]): Set<THREE.Material> {
    const exempt = new Set<THREE.Material>();
    for (const m of matches) {
      const entry = cupMap.get(m.cup.id);
      if (!entry) continue;
      const body = entry.group.children.find(
        (c): c is THREE.Mesh =>
          c instanceof THREE.Mesh && c.userData.role === "body",
      );
      if (body && !Array.isArray(body.material)) exempt.add(body.material);
    }
    return exempt;
  }

  hud.onSearchInput((query) => {
    const matches = searchCups(cups, query);
    hud.setSearchResults(query, matches);
    activeMatches = matches;
    // Empty query OR no-matches → restore. The latter avoids dimming
    // the whole shelf as punishment for typing nonsense; the dropdown's
    // "No matches" is enough signal without going dark.
    if (query.trim() === "" || matches.length === 0) {
      clearSpotlight(world.scene);
      return;
    }
    // Everything else (shelf, floor, cat, POIs, non-matching cups,
    // shared handle/inner materials) gets dimmed so the matches pop as
    // the only fully-lit objects.
    applySpotlight(world.scene, buildSpotlightExempt(matches));
  });

  hud.onSearchSelect((cupId) => {
    const cup = cups.find((c) => c.id === cupId);
    if (!cup) return;
    // Clear the spotlight + close the dropdown so focus mode starts clean.
    clearSpotlight(world.scene);
    activeMatches = [];
    hud.clearSearch();
    selectCup(cup);
  });

  return {
    reapplyForLateMaterials: () => {
      if (activeMatches.length > 0) {
        applySpotlight(world.scene, buildSpotlightExempt(activeMatches));
      }
    },
  };
}
